import React from 'react'
import { P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { RoleSection, RoleTemplate, RoleTitle, RoleUl } from '../RoleTemplate'

const WillWriter = (): React.JSX.Element => (
  <RoleTemplate
    roleName="Software Engineering Lead"
    roleUrl="https://dignity.kallidusrecruit.com/vacancyinformation.aspx?vguid=c4c5c390-e97d-416e-8aa9-6536c8bf7bb9"
    location="Maidenhead, Hybrid (3 days on-site)"
    salary="Up to £110,000 per annum"
    contractType="Full-time, Permanent"
    closingDate="30th August 2025"
    content={
      <>
        <RoleSection>
          <RoleTitle>The Role</RoleTitle>
          <P>
            At Dignity and Farewill, we&apos;re redefining what end-of-life
            services can look and feel like — modern, compassionate, accessible.
            As we combine the scale and experience of Dignity with
            Farewill&apos;s tech-driven innovation, we&apos;re building a
            next-generation platform to support families at one of life&apos;s
            most important moments.
          </P>
          <P>
            We&apos;re looking for a Software Engineering Lead to join our
            growing technology team and help drive this transformation. This is
            a hands-on technical leadership role, responsible for guiding one of
            our key product domains — whether that&apos;s Funeral Plans, Legal
            Services, or At-Need Funerals — from strategy through to scalable,
            secure software delivery.
          </P>
        </RoleSection>
        <RoleSection margin={[GTR.M, 0]}>
          <RoleTitle>What you&apos;ll be doing:</RoleTitle>
          <RoleUl>
            <li>
              Lead and mentor a team of engineers focused on delivering
              impactful, resilient software products.
            </li>
            <li>
              Define and implement the technical strategy for your domain,
              aligned to our roadmap and vision.
            </li>
            <li>
              Ensure high standards of engineering quality, security,
              architecture, and agile delivery.
            </li>
            <li>
              Collaborate closely with Product, Design, and cross-functional
              teams to deliver business outcomes.
            </li>
            <li>
              Get stuck into code, architecture, and problem-solving when needed
              — you&apos;re still close to the tech.
            </li>
            <li>
              Coach and mentor engineers, helping them grow in a
              high-performing, compassionate culture.
            </li>
          </RoleUl>
        </RoleSection>
        <RoleSection>
          <RoleTitle>About you:</RoleTitle>
          <P>
            You&apos;re a pragmatic engineering leader who knows how to balance
            tech quality with business value. You&apos;ve led OR mentored
            high-performing teams, delivered complex products, and enjoy being
            the person who connects big-picture technical thinking with real
            user needs.
          </P>
          <P>We&apos;d love to hear from you if you:</P>
          <RoleUl>
            <li>
              Have moved from Senior Engineer into Tech Lead / Engineering
              Manager roles.
            </li>
            <li>
              Are comfortable leading strategy but still enjoy being close to
              architecture and implementation.
            </li>
            <li>
              Have worked with JavaScript/TypeScript, Node.js, SQL, React or
              similar technologies.
            </li>
            <li>
              Bring strong experience in backend engineering, APIs, cloud
              infrastructure (we use AWS), and modern DevOps.
            </li>
            <li>
              Have experience building products in high-stakes or regulated
              domains (e.g. fintech, legal tech, health tech).
            </li>
            <li>
              Thrive in cross-functional teams and believe in shipping fast,
              learning fast, and iterating often.
            </li>
            <li>
              Are passionate about team health, development, and a culture of
              continuous improvement.
            </li>
          </RoleUl>
        </RoleSection>
        <RoleSection>
          <RoleTitle>What we&apos;re looking for:</RoleTitle>
          <RoleUl>
            <li>Proven delivery of secure, scalable technical products.</li>
            <li>
              Background in backend/system architecture and cloud-native
              development.
            </li>
            <li>
              Familiarity with modern SDLC: CI/CD, containerisation,
              infrastructure as code.
            </li>
            <li>
              An excellent communicator and problem-solver — calm under
              pressure, collaborative by nature.
            </li>
            <li>
              Willingness to work hybrid from our Maidenhead offices (minimum 3
              days per week).
            </li>
          </RoleUl>
        </RoleSection>
        <RoleSection>
          <RoleTitle>Why join us:</RoleTitle>
          <P>
            This is a rare opportunity to join a company that&apos;s genuinely
            making a difference in people&apos;s lives — modernising a
            traditionally underserved space, with empathy and innovation at its
            core.
          </P>
          <P>
            You&apos;ll be joining a supportive, forward-thinking environment
            where:
          </P>
          <RoleUl>
            <li>Engineering is valued at every level of the organisation.</li>
            <li>
              You&apos;ll have real ownership and input into our tech strategy.
            </li>
            <li>
              We believe in kindness, autonomy, and impact — and back that up
              with flexible, human-centred ways of working.
            </li>
            <li>
              You&apos;ll be part of a mission to reimagine how people
              experience end-of-life planning, funerals, and remembrance.
            </li>
          </RoleUl>
        </RoleSection>
      </>
    }
  />
)

export default WillWriter
